import {createTheme, type Theme} from '@mui/material'

// ── Theme tokens ────────────────────────────────────────────────

interface ThemeTokens {
  sidebar: {
    background: string
    border: string
    width: number
  }
  surface: {
    background: string
    panel: string
    panelDeep: string
    glass: string
  }
  text: {
    primary: string
    secondary: string
    muted: string
  }
  accordion: {
    border: string
    borderStrong: string
    expandIcon: string
    sectionIcon: string
  }
  accent: {
    primary: string
    light: string
    lightest: string
    dark: string
    glow: string
  }
}

const darkTokens: ThemeTokens = {
  sidebar: {
    background: '#14111F',
    border: 'rgba(255, 255, 255, 0.08)',
    width: 300,
  },
  surface: {
    background: '#07060B',
    panel: 'rgba(20, 17, 31, 0.72)',
    panelDeep: 'rgba(28, 24, 44, 0.85)',
    glass: 'rgba(20, 17, 31, 0.72)',
  },
  text: {
    primary: '#F5F3FF',
    secondary: '#B8B3CC',
    muted: '#6F6A85',
  },
  accordion: {
    border: 'rgba(255, 255, 255, 0.08)',
    borderStrong: 'rgba(255, 255, 255, 0.14)',
    expandIcon: '#6F6A85',
    sectionIcon: '#B8B3CC',
  },
  accent: {
    primary: '#7C5CFF',
    light: '#A78BFA',
    lightest: '#C4B5FD',
    dark: '#4F3DD9',
    glow: 'rgba(124, 92, 255, 0.45)',
  },
}

const lightTokens: ThemeTokens = {
  sidebar: {
    background: '#f5f5f5',
    border: 'rgba(0, 0, 0, 0.08)',
    width: 300,
  },
  surface: {
    background: '#ffffff',
    panel: 'rgba(255, 255, 255, 0.9)',
    panelDeep: 'rgba(245, 245, 250, 0.95)',
    glass: 'rgba(255, 255, 255, 0.85)',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    muted: 'rgba(0, 0, 0, 0.4)',
  },
  accordion: {
    border: 'rgba(0, 0, 0, 0.08)',
    borderStrong: 'rgba(0, 0, 0, 0.14)',
    expandIcon: 'rgba(0, 0, 0, 0.4)',
    sectionIcon: 'rgba(0, 0, 0, 0.54)',
  },
  accent: {
    primary: '#7C5CFF',
    light: '#A78BFA',
    lightest: '#C4B5FD',
    dark: '#4F3DD9',
    glow: 'rgba(124, 92, 255, 0.35)',
  },
}

const themeTokensMap = {
  dark: darkTokens,
  light: lightTokens,
} as const

type ThemeMode = keyof typeof themeTokensMap

// ── MUI theme factory ───────────────────────────────────────────

const createAppTheme = (mode: ThemeMode): Theme => {
  const tokens = themeTokensMap[mode]
  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.accent.primary,
        light: tokens.accent.light,
        dark: tokens.accent.dark,
        contrastText: '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor:
              mode === 'dark' ? 'rgba(255, 255, 255, 0.15) transparent' : 'rgba(0, 0, 0, 0.2) transparent',
          },
          '*::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.2)',
            borderRadius: '3px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.35)',
          },
        },
      },
    },
  })
}

// ── Hook for dynamic token access ───────────────────────────────

import {useTheme} from '@mui/material'

const useAppTokens = (): ThemeTokens => {
  const theme = useTheme()
  const mode = theme.palette.mode as ThemeMode
  return themeTokensMap[mode]
}

// ── Exports ─────────────────────────────────────────────────────

export type {ThemeTokens, ThemeMode}
export {createAppTheme, useAppTokens, darkTokens, lightTokens}
